"use client";

import { motion, useScroll, useSpring, useTransform } from "framer-motion";
import Image from "next/image";
import { useRef } from "react";

interface CardData {
  subtitle: string;
  title: string;
  description: string;
  imageSrc: string;
}

const cardsData: CardData[] = [
  {
    subtitle: "JVL® Cabinets",
    title: "Flex Cabinets",
    description:
      "FLEX is here and is one of JVL's® premium cabinet models. Designed for an exceptional gaming experience, users can enjoy a display with high-resolution 4K Ultra-Bright visuals and game-driven full RGB LED accent lights. Fit with wireless phone charging, this machine is perfect for getting customers to sit down and stay until they win big. View JVL® cabinets here.",
    imageSrc: "/timemachine.webp",
  },
  {
    subtitle: "Betson Southeast",
    title: "Light and Wonder",
    description:
      "Betson Southeast proudly represents Light & Wonder in the Georgia COAM market, offering top-performing game titles tailored for local players. Favorites like GOLD FISH FEEDING TIME® TREASURE, ZEUS®, BIER HAUS®, and THE GREEN MACHINE® are featured on the award-winning KASCADA® Dual Screen cabinet. Since 2013, the Georgia Lottery Corporation has regulated Class A and B COAMs, ensuring strict compliance with licensing, financial, and communication standards. Its COAM Division also emphasizes education and ongoing support for license holders to maintain industry integrity.",
    imageSrc: "/light-wonder.webp",
  },
  {
    subtitle: "Bad Dog Games",
    title: "Pressto Skill Cabinet",
    description: `The Pressto Skill Cabinet from Bad Dog Games offers a more natural and engaging gaming experience with its patent-pending technology. It features four game-reactive LED zones that enhance player interaction and increase play rates from 3–4 to 5–6 plays per minute. Key components include a 43" HD PCAP touchscreen, IT NV200 bill validator, wireless and USB phone charging, and optional TITO support. Designed for high performance and immersion, Pressto cabinets deliver reliable, winning entertainment.`,
    imageSrc: "/pressto-skill-cabinet.webp",
  },
  {
    subtitle: "Aurora",
    title: "Aurora 8 Classic",
    description: `Now available for the Georgia market – the Aurora 8 Classic, a 9-in-1 skill-based game board built for vertical monitors. Designed to elevate player excitement, it features Dual SAS technology for seamless integration and performance. Game titles include Lucky Fella, Above the Sky, Jackpot Mania, Super Party, Metal Heart, and more. With a diverse lineup, players can enjoy nonstop entertainment and variety. The Aurora 8 Classic is your next go-to for skill gaming fun!`,
    imageSrc: "/aurora.webp",
  },
  {
    subtitle: "Banilla® COAMS",
    title: "The Fusions Line & Skyriser",
    description: `Banilla Games® offers diverse and competitive COAM machines, including the popular Fusions Line and Skyriser models—each featuring a 5-game multi-pack. Built with advanced graphics, premium audio, and a sleek design, these cabinets are made to attract and engage players. The Fusions Line includes multiple versions with exclusive new titles, while the Skyriser delivers a true Vegas-style gaming experience anywhere. Explore Banilla® cabinets and bring top-tier entertainment to your location.`,
    imageSrc: "/banilla.webp",
  },
  {
    subtitle: "Primero® Cabinets",
    title: "Bar-X Cabinet",
    description: `The Bar-X is here and is one of Primero’s® premium cabinet models. Designed for an exceptional gaming experience, users can enjoy a display with high-resolution visuals and digital gameplay. Fit with a USB charger and cup holder, this machine is perfect for getting customers to sit down and stay until they win big. View Primero® cabinets here.`,
    imageSrc: "/primero.webp",
  },
];

export default function ScrollStackSection() {
  const containerRef = useRef<HTMLDivElement>(null);

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"],
  });

  // Apply spring physics for smoother animation
  const smoothProgress = useSpring(scrollYProgress, {
    stiffness: 40,
    damping: 25,
    restDelta: 0.001,
  });

  // Calculate heights - 100vh per card + 100vh buffer
  const cardCount = cardsData.length;
  const trackHeight = `${100 + cardCount * 60}vh`;

  return (
    <section
      ref={containerRef}
      className="bg-brand-black relative w-full"
      style={{ height: trackHeight }}
    >
      <div className="sticky top-0 flex h-screen w-full flex-col items-center justify-center px-5 py-8 md:px-12 lg:px-[100px] lg:py-12">
        {/* Cards Stack */}
        <div className="relative h-full w-full">
          {cardsData.map((card, index) => (
            <ScrollCard
              key={index}
              card={card}
              index={index}
              totalCards={cardCount}
              scrollYProgress={smoothProgress}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

interface ScrollCardProps {
  card: CardData;
  index: number;
  totalCards: number;
  scrollYProgress: ReturnType<typeof useSpring>;
}

function ScrollCard({
  card,
  index,
  totalCards,
  scrollYProgress,
}: ScrollCardProps) {
  const isFirstCard = index === 0;

  // Each card gets an equal slice of the scroll progress
  // We leave the last 15% of scroll as "rest" so final card fully settles
  const usableScroll = 0.85;
  const segmentSize = usableScroll / totalCards;
  const startRange = index * segmentSize;
  const endRange = startRange + segmentSize;

  // Each card stacks with a small offset
  // Using a slightly larger offset on desktop for better visibility
  const stackedTop = index * 4;

  const y = useTransform(
    scrollYProgress,
    isFirstCard
      ? [0, 1]
      : [
          startRange,
          startRange + segmentSize * 0.4,
          startRange + segmentSize * 0.8,
          endRange,
        ],
    isFirstCard ? [0, 0] : [100, 60, stackedTop + 4, stackedTop],
    { clamp: true }
  );

  const yVh = useTransform(y, (value) => `${value}vh`);

  const opacity = useTransform(
    scrollYProgress,
    isFirstCard
      ? [0, 1]
      : [startRange, startRange + segmentSize * 0.25, endRange],
    isFirstCard ? [1, 1] : [0, 1, 1]
  );

  const scaleEffect = useTransform(
    scrollYProgress,
    isFirstCard
      ? [0, segmentSize]
      : [startRange, endRange, Math.min(endRange + segmentSize, 1)],
    isFirstCard ? [1, 0.97] : [0.95, 1, 0.97]
  );

  return (
    <motion.div
      style={{
        y: yVh,
        opacity,
        scale: scaleEffect,
        willChange: "transform, opacity",
      }}
      className="absolute inset-0 flex w-full items-start justify-center md:items-center md:pt-0"
    >
      {/* Card Container */}
      <div className="min-h-[70vh] w-full rounded-[20px] bg-linear-to-br from-[#EE4137] to-[#A10900] p-5 py-6 shadow-[0_-10px_40px_rgba(0,0,0,0.5)] md:min-h-auto md:p-10 md:py-12 lg:p-10 lg:py-14">
        <div className="flex flex-col lg:flex-row lg:items-center lg:gap-10">
          {/* Text Content */}
          <div className="order-2 flex-1 lg:order-1">
            <p className="font-raleway mb-1 text-sm font-normal text-white/80 md:mb-2 md:text-base">
              {card.subtitle}
            </p>
            <h3 className="font-raleway mb-3 text-xl font-bold text-white md:mb-4 md:text-[32px]">
              {card.title}
            </h3>
            <p className="font-raleway text-sm leading-relaxed text-white/90 md:text-base">
              {card.description}
            </p>
          </div>

          {/* Image */}
          <div className="order-1 mb-6 flex justify-center lg:order-2 lg:mb-0 lg:w-[300px] lg:shrink-0">
            <div className="relative h-[180px] w-[180px] md:h-[250px] md:w-[250px] lg:h-[300px] lg:w-[300px]">
              <Image
                src={card.imageSrc}
                alt={card.title}
                fill
                className="object-contain"
              />
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
